import React, { useState } from 'react';
import { Fade, Slide } from 'react-awesome-reveal';
import { saveLead, saveExpertRequest } from '../controllers/useLandingPageController';
import '../context/font.css';
import { useNavigate } from "react-router-dom";
import ReportBugButton from '../components/ReportBugButton';
import Seo from '../components/Seo';

const FeatureSection = ({ title, description, features, buttonText, visual, isReversed, gradient, onButtonClick }) => {
  return (
    <div className={`flex flex-col items-center gap-8 lg:gap-12 ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
      {/* Bagian Gambar */}
      <div className="lg:w-1/2 w-full px-4 lg:px-0 order-1 lg:order-none">
        <div className="relative transform perspective-1000 rotate-y-20 max-w-[600px] mx-auto">
          <div className={`absolute inset-0 bg-gradient-to-tr ${gradient} rounded-3xl blur-2xl opacity-30`}></div>
          <div className="relative overflow-hidden rounded-3xl">
            {React.cloneElement(visual, {
              className: `${visual.props.className} w-full h-auto object-contain`
            })}
          </div>
        </div>
      </div>

      {/* Bagian Konten */}
      <div className="lg:w-1/2 w-full px-4 lg:px-0 order-2 lg:order-none">
        <h3 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 font-serif animate-text-pop">{title}</h3>
        <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base font-sans">{description}</p>
        <ul className="text-gray-600 list-disc list-inside mb-4 md:mb-6 text-sm md:text-base font-sans space-y-2">
          {features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
        <button
          onClick={onButtonClick}
          className="bg-sky-600 hover:bg-sky-700 px-6 py-2 md:px-8 md:py-3 rounded-full text-sm md:text-lg font-semibold text-white transition-all font-sans"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const DetailedFeaturesSection = ({ featuresData }) => {
  return (
    <section className="container mx-auto px-4 md:px-6 py-16 md:py-20">
      <Fade triggerOnce>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16">Detailed Features</h2>
      </Fade>
      <div className="space-y-10 md:space-y-20">
        {featuresData.map((feature, index) => (
          <Slide key={index} direction="up" triggerOnce delay={index * 100}>
            <FeatureSection {...feature} />
          </Slide>
        ))}
      </div>
    </section>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [trackedFeature, setTrackedFeature] = useState('');
  const featuresData = [
    {
      title: "Smart CV Builder",
      description: "Create professional CVs in minutes with our intuitive builder. Choose from multiple templates, customize layouts, and get AI-powered suggestions to optimize your content for different job roles.",
      features: [
        "Create multiple CVs for different job applications",
        "AI-powered content optimization",
        "Real-time formatting and layout adjustments",
        "Export to PDF or share directly with employers"
      ],
      buttonText: "Start Building Your CV",
      visual: <img src="/section1.png" alt="Section1" className="drop-shadow-xl" loading="lazy" />,
      gradient: "from-sky-200 to-purple-200",
      onButtonClick: () => {
        navigate("/dashboard")
        // setShowComingSoonModal(true);
        // setTrackedFeature("Smart CV Builder");
      }
    },
    {
      title: "Your Digital Profile",
      description: "Showcase your professional achievements with a personalized webpage that serves as your digital CV. Share your profile link with potential employers and stand out from the crowd.",
      features: [
        "Customizable profile page",
        "Integration with LinkedIn and other platforms",
        "Analytics to track profile views",
        "SEO optimization for better visibility"
      ],
      buttonText: "Create Your Profile",
      visual: <img src="/section2.png" alt="Section2" className="drop-shadow-xl" loading="lazy" />,
      gradient: "from-purple-200 to-blue-200",
      onButtonClick: () => {
        navigate("/dashboard")
        // setShowComingSoonModal(true);
        // setTrackedFeature("Your Digital Profile");
      },
      isReversed: true
    },
    // {
    //   title: "Expert Career Guidance",
    //   description: "Get personalized advice from career experts to improve your CV, prepare for interviews, and ace technical tests. Our specialists will help you navigate the job market with confidence.",
    //   features: [
    //     "1-on-1 consultation sessions",
    //     "Mock interviews with feedback",
    //     "Technical test preparation",
    //     "Career path planning"
    //   ],
    //   buttonText: "Book a Session",
    //   visual: <img src="/section3.png" alt="Section3" className="drop-shadow-xl" loading="lazy" />,
    //   gradient: "from-blue-200 to-sky-200",
    //   onButtonClick: () => {
    //     setShowModal(true);
    //     setTrackedFeature("Expert Career Guidance");
    //   }
    // }
  ];

  const handleComingSoonSubmit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await saveLead(email, trackedFeature);
      
      if (error) throw error;
      
      alert('Thank you! We will notify you when features are available!');
      setShowComingSoonModal(false);
      setEmail('');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save data. Please try again.');
    }
  };

  const handleExpertSubmit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await saveExpertRequest(email, phone, trackedFeature);
      
      if (error) throw error;
      
      alert('Thank you! Our expert will contact you soon!');
      setShowModal(false);
      setEmail('');
      setPhone('');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save data. Please try again.');
    }
  };
  
  return (
    <>
    {/* <Seo  
        title="Create Free CV & Digital Portfolio" 
        description="Build your professional CV and online portfolio in minutes. 100% free with premium templates and expert guidance."
      /> */}
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4f8] to-[#ffffff] text-gray-800">
      {/* Hero Section */}
      <header className="container mx-auto px-4 md:px-6 py-12 md:py-16">
        <nav className="flex justify-between items-center mb-12 md:mb-20">
          {/* <img src="/logo_portoize.png" alt="Portoize" className="w-32 md:w-48" loading="lazy" /> */}
          <span className="flex items-center gap-1">
            <img src="/logo_portoize.png" alt="Portoize" className="w-32 md:w-48" loading="lazy" />
            <div className="bg-blue-500 text-white px-2 py-1 rounded-full leading-none font-medium">
              BETA
            </div>
          </span>
          <button onClick={() => navigate("/dashboard")} className="bg-sky-600 hover:bg-sky-700 px-4 py-2 md:px-6 md:py-2 rounded-full text-sm md:text-base text-white transition-all font-sans">
            Early Access
          </button>
        </nav>

        <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 mt-3">
          <div className="lg:w-1/2 w-full relative px-4 md:px-0">
            <div className="absolute -top-16 -left-16 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply opacity-40 blur-3xl animate-pulse"></div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight font-serif animate-text-slide">
              <span>Your All-in-One Career Platform</span>
              <br />
              <span className="text-sky-600">Build, Optimize, and Land Your Dream Job</span>
            </h1>
            <p className="text-gray-600 text-sm md:text-base mb-6 md:mb-8 font-sans">
              Portoize empowers you to create professional CVs, build your digital profile,
              and get expert guidance to ace interviews and technical tests. Start your
              journey to career success today!
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <button onClick={() => navigate("/dashboard")} className="bg-sky-600 hover:bg-sky-700 px-6 py-3 md:px-8 md:py-4 rounded-full text-sm md:text-lg font-semibold text-white transition-all hover:scale-105 font-sans">
                Build Your CV Now
              </button>
              {/* <button
                onClick={() => setShowModal(true)}
                className="border-2 border-sky-600 text-sky-600 hover:bg-sky-600/10 px-6 py-3 md:px-8 md:py-4 rounded-full text-sm md:text-lg font-semibold transition-all font-sans"
              >
                Get Expert Help
              </button> */}
            </div>
          </div>

          <div className="hidden lg:block lg:w-1/2 px-4 md:px-0">
            <img 
              src="/hero.png" 
              alt="Hero" 
              className="w-full h-auto max-w-[600px] mx-auto drop-shadow-xl" 
              loading="lazy"
            />
          </div>
        </div>
      </header>

      {/* Detailed Features Section */}
      <DetailedFeaturesSection featuresData={featuresData} />

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="container mx-auto px-4 md:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-sky-600 mb-4 font-serif">Portoize</h3>
              <p className="text-gray-600 font-sans">Your career success starts here.</p>
            </div>
            {/* <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 font-serif">Quick Links</h4>
              <ul className="space-y-2 font-sans">
                <li><a href='/' className="text-gray-600 hover:text-sky-600">About Us</a></li>
                <li><a href='/' className="text-gray-600 hover:text-sky-600">Features</a></li>
                <li><a href='/' className="text-gray-600 hover:text-sky-600">Pricing</a></li>
                <li><a href='/' className="text-gray-600 hover:text-sky-600">Contact</a></li>
              </ul>
            </div> */}
            {/* <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 font-serif">Support</h4>
              <ul className="space-y-2 font-sans">
                <li><a href='/' className="text-gray-600 hover:text-sky-600">Help Center</a></li>
                <li><a href='/' className="text-gray-600 hover:text-sky-600">FAQ</a></li>
                <li><a href='/' className="text-gray-600 hover:text-sky-600">Privacy Policy</a></li>
              </ul>
            </div> */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-4 font-serif">Contact Us</h4>
              <p className="text-gray-600 font-sans">Email: portoizeid@gmail.com</p>
              {/* <p className="text-gray-600 font-sans">Phone: +123 456 7890</p> */}
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center text-gray-600 font-sans">
            © 2025 Portoize. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Modal for Coming Soon */}
      {showComingSoonModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 max-w-md w-full border border-gray-200 mx-4">
            <h3 className="text-2xl font-bold mb-6 font-serif">Feature Coming Soon!</h3>
            <form onSubmit={handleComingSoonSubmit} className="space-y-6">
              <div>
                <label className="block text-sm mb-2 font-sans">Email Address</label>
                <input
                  type="email"
                  required
                  className="w-full bg-gray-50 rounded-lg px-4 py-3 border border-gray-200 focus:border-sky-600 focus:ring-sky-600 font-sans"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowComingSoonModal(false)}
                  className="flex-1 px-6 py-3 rounded-lg border border-gray-200 hover:border-sky-600 transition-all font-sans"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white transition-all font-sans"
                >
                  Notify Me
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Expert Help */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 max-w-md w-full border border-gray-200 mx-4">
            <h3 className="text-2xl font-bold mb-6 font-serif">Get Expert Help</h3>
            <form onSubmit={handleExpertSubmit} className="space-y-6">
              <div>
                <label className="block text-sm mb-2 font-sans">Email Address</label>
                <input
                  type="email"
                  required
                  className="w-full bg-gray-50 rounded-lg px-4 py-3 border border-gray-200 focus:border-sky-600 focus:ring-sky-600 font-sans"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm mb-2 font-sans">Phone Number</label>
                <input
                  type="tel"
                  required
                  className="w-full bg-gray-50 rounded-lg px-4 py-3 border border-gray-200 focus:border-sky-600 focus:ring-sky-600 font-sans"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 rounded-lg border border-gray-200 hover:border-sky-600 transition-all font-sans"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white transition-all font-sans"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ReportBugButton />
    </div>
    </>
  );
};

export default LandingPage;